<script>
import axios from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCaretDown,
  faCaretUp,
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import EsLoader from '@/components/EsLoader.vue';
import LegendNearMatch from '@/components/LegendNearMatch.vue';
import NearMatchResolveCard from '@/components/NearMatchResolveCard.vue';
import BreadcrumbsManager from '@/mixins/BreadcrumbManager';
import NotificationManager from '@/mixins/NotificationManager';

library.add(
  faCaretDown,
  faCaretUp,
  faExclamationTriangle,
);

export default {
  mixins: [BreadcrumbsManager, NotificationManager],
  components: {
    EsLoader,
    LegendNearMatch,
    NearMatchResolveCard,
  },
  data() {
    return {
      filterExpanded: null,
      loading: false,
      lowerBound: null,
      nearMatches: [],
      organizations: [],
      nearMatchCount: null,
      page: 1,
      properties: [],
      submissionCount: null,
      totalNearMatches: null,
      upperBound: null,
    };
  },
  created() {
    this.filterExpanded = false;
    this.getMatches();
    this.getProperties();
    this.getOrganizations();
    this.getOverview();
    this.setBreadcrumb([
      { text: 'Home', to: { name: 'groupList' } },
      { text: 'Pending Near Match', to: { } },
    ]);
  },
  mounted() {
    this.checkNotification();
  },
  computed: {
    matchSearchParams() {
      const params = {};
      params.page = this.page;
      params.pageSize = 5;
      params.open = true;

      const sorters = {};
      sorters.field = 'transDate';
      sorters.order = 'DESC';
      const sort = [];
      sort.push(sorters);
      params.sort = JSON.stringify(sort);
      return params;
    },
    moreToLoad() {
      if (this.singleNearMatch || this.totalNearMatches === this.nearMatches.length) {
        return false;
      }
      return true;
    },
    numberNearMatches() {
      if (this.$route.params.nearMatchId) {
        this.totalNearMatches = 1;
      }
      return this.totalNearMatches;
    },
    singleNearMatch() {
      if (this.$route.params.nearMatchId) {
        return true;
      }
      return false;
    },
  },
  methods: {
    checkNotification() {
      const { message } = this.$route.params;
      if (this.$route.params.message) {
        this.showNotification('success', [message]);
      }
    },
    loadMore() {
      this.page += 1;
      this.getMatches();
    },
    maxPercent(nearMatch) {
      let percentStage = 0;
      let percentHigh = 0;
      for (let i = 0; i < nearMatch.locationMatchTransIds.length; i += 1) {
        percentStage = nearMatch.locationMatchTransIds[i].matchPercent;
        if (percentStage > percentHigh) {
          percentHigh = percentStage;
        }
      }
      return percentHigh;
    },
    test(a) {
      if (!this.$route.params.nearMatchId) {
        const { request } = a.locationMatchTransDetail;
        return request;
      }
      const { request } = a;
      return request;
    },
    toggleFilter() {
      this.filterExpanded = !this.filterExpanded;
    },
    toggleLoading() {
      this.loading = !this.loading;
    },
    async getOrganizations() {
      try {
        const res = await axios.get('organizations');
        this.organizations = res.data;
      } catch {
        // handle error
      }
    },
    async getOverview() {
      try {
        const res = await axios.get(`search/location/nearmatches/overview`);
        this.submissionCount = res.data.submissions;
        this.nearMatchCount = res.data.nearMatches;
      } catch {
        // handle error
      }
    },
    async getMatches() {
      try {
        this.toggleLoading();
        // TODO string too long
        if (this.$route.params.nearMatchId) {
          const res = await axios.get(`search/location/nearmatches/${this.$route.params.nearMatchId}/details`);
          this.nearMatches = (res.data);
        } else {
          const queryParams = new URLSearchParams();
          Object.keys(this.matchSearchParams).forEach((key) => {
            queryParams.append(key, this.matchSearchParams[key]);
          });
          const res = await axios.get('search/location/nearmatches', { params: queryParams });
          for (let i = 0; i < res.data.data.length; i += 1) {
            this.nearMatches.push(res.data.data[i]);
          }
          this.totalNearMatches = res.data.totalCount;
        }
        this.toggleLoading();
      } catch {
        // handle error
      }
    },
    async getProperties() {
      try {
        const res = await axios.get('properties');
        this.properties = res.data;

        const upperBoundObject = this.properties[this.properties.map(o => { return o.property; }).indexOf('UPPER_BOUND_FOR_NEAR_MATCHES')];
        this.upperBound = upperBoundObject.value;

        const lowerBoundObject = this.properties[this.properties.map(o => { return o.property; }).indexOf('LOWER_BOUND_FOR_NEAR_MATCHES')];
        this.lowerBound = lowerBoundObject.value;
      } catch {
        // handle error
      }
    },
  },
};
</script>

<template>
  <div class="document-container">
    <div class="document">
      <div class="near-match-box">
        <div class="header-row is-flex">
          <div class="warning is-flex flex-align--center flex-justify--center">
            <font-awesome-icon
              class="has-text-white fa-2x"
              :icon="['fas', 'exclamation-triangle']"
            />
          </div>
          <div class="header-row--heading is-flex">
            <div class="is-flex header-row--description grow">
              <div
                class="is-flex header-row--cell flex-align--center
                  has-text-weight-bold is-size-4 nowrap"
              >
                NEAR MATCHES FOUND:
              </div>
              <div class="header-row--cell grow has-text-weight-bold has-text-left border--left">
                <div class="has-text-grey-light">
                  The system found Near Matches which should be reviewed and resolved by a user.
                </div>
                <div class="">
                  Next Steps: Review Near Matches below
                </div>
              </div>
            </div>
            <div
              class="is-flex header-row--cell flex-direction--column
                flex-justify--center border--left"
            >
              <div
                v-if="loading"
                class="has-text-weight-bold is-size-5"
              >
                --
              </div>
              <div
                v-else
                class="has-text-weight-bold is-size-5"
              >
                {{ submissionCount }}
              </div>
              <div class="nowrap is-size-7 has-text-grey">
                Submission Records
              </div>
            </div>
            <div
              class="is-flex header-row--cell flex-direction--column
                flex-justify--center border--left"
            >
              <div
                v-if="loading"
                class="has-text-weight-bold is-size-5"
              >
                --
              </div>
              <div
                v-else
                class="has-text-weight-bold is-size-5"
              >
                {{ nearMatchCount }}
              </div>
              <div class="nowrap is-size-7 has-text-grey">
                Near Matches
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="match-title is-flex has-text-grey">Pending Near Matches</div>
    <near-match-resolve-card
      v-if="singleNearMatch && nearMatches.request"
      :id="nearMatches.requestId"
      :organizations="organizations"
      :request="nearMatches.request"
      :response="nearMatches.response"
      :singleMatch="true"
    />
    <near-match-resolve-card
      v-else
      v-for="nearMatch in nearMatches"
      :key="nearMatch.id"
      :id="nearMatch.id"
      :lowerBound="lowerBound"
      :matchPercent="maxPercent(nearMatch)"
      :organizations="organizations"
      :request="test(nearMatch)"
      :singleMatch="false"
      :upperBound="upperBound"
    />
    <a
      v-if="moreToLoad && !loading"
      @click="loadMore"
    >
      Load More
    </a>
    <es-loader v-if="loading"/>
    <legend-near-match/>
  </div>
</template>

<style lang="sass" scoped>
  @import "~bulma/sass/utilities/mixins"
  +touch
    .header-row--description
      flex-direction: column
      border-left: none
  .document-container
    margin-left: auto
    margin-right: auto
  .header-row--heading
    width: 100%
    padding: 0.5rem 0
    border-bottom: 1px solid #eee
  .header-row--cell
    padding: 0 1rem
  .header-row--button
    padding: 1rem
  .border--left
    border-left: 1px solid #ccc
  .match-title
    padding: 1rem 0 1rem
  .header-row
    min-height: 60px
    .warning
      background-color: red
      min-height: 60px
      min-width: 60px
  .border-right--dashed
    border-right: 1px dashed #ccc
  .filter-row--container
    padding-right: 1rem
  .is-fullwidth
    width: 100%

  .filter-box
    min-width: 190px
    padding: 0 1rem
    margin-bottom: 1rem
  .no-padding
    padding: 0

</style>
